// ─────────────────────────────────────────────────────────────────────────────
// Canonical enum values for every entity field declared in GRC_ARCHITECTURE.md
// §4. Order matches the declaration order in the schema — the progression
// encodes the lifecycle of each enum.
// ─────────────────────────────────────────────────────────────────────────────

// Risk.status (§4 Risk)
export const RISK_STATUSES = [
  'Identified',
  'Being Treated',
  'Treated',
  'Accepted',
  'Closed',
];

// Nonconformity.status (§4 Nonconformity)
export const NC_STATUSES = [
  'Open',
  'In Progress',
  'Closed',
];

// Incident.status (§4 Incident)
export const INCIDENT_STATUSES = [
  'Open',
  'Investigating',
  'Contained',
  'Resolved',
  'Closed',
];

// Document.status (§4 Document)
export const DOCUMENT_STATUSES = [
  'Draft',
  'In Review',
  'Approved',
  'Superseded',
  'Retired',
];

// Audit.status (§4 Audit)
export const AUDIT_STATUSES = [
  'Planned',
  'In Progress',
  'Completed',
];

// ManagementReview.decisions[].status and .actions[].status (§4 ManagementReview)
export const MR_STATUSES = [
  'Pending',
  'In Progress',
  'Completed',
];

// Objective.status (§4 Objective)
export const OBJECTIVE_STATUSES = [
  'Not Started',
  'On Track',
  'At Risk',
  'Achieved',
  'Not Achieved',
];

// Asset.status (§4 Asset)
export const ASSET_STATUSES = [
  'Active',
  'Decommissioned',
  'Under Review',
];

// Supplier.type (§4 Supplier)
export const SUPPLIER_TYPES = [
  'Cloud',
  'SaaS',
  'IT Services',
  'Professional Services',
  'Hardware',
  'Other',
];

// Supplier.status (§4 Supplier)
export const SUPPLIER_STATUSES = [
  'Active',
  'Under Review',
  'Terminated',
];

// Supplier.criticality (§4 Supplier)
export const CRITICALITY_LEVELS = [
  'Critical',
  'High',
  'Medium',
  'Low',
];

// Authority.type (§4 Authority — ISO 27001 control A.5.5)
export const AUTHORITY_TYPES = [
  'Data protection authority',
  'National CSIRT',
  'Sector regulator',
  'Law enforcement',
  'NIS2 competent authority',
  'Critical infrastructure authority',
  'Standardisation body',
  'Government cyber agency',
  'Emergency response',
  'Other',
];

// Authority.purpose[] (§4 Authority — ISO 27001 control A.5.5)
export const AUTHORITY_PURPOSES = [
  'Personal data breach notification',
  'NIS2 incident notification',
  'Sector-specific incident reporting',
  'Cyber threat intelligence sharing',
  'Vulnerability disclosure / coordination',
  'Regulatory inquiry / supervision',
  'Certification or accreditation body',
  'Law enforcement coordination',
  'Standards / best-practice consultation',
  'Physical security emergency contact',
  'Other',
];

// Authority.status (§4 Authority — ISO 27001 control A.5.5)
// Shared with Special interest groups (A.5.6) for the status field.
export const AUTHORITY_STATUSES = [
  'Active',
  'Inactive',
];

// SpecialInterestGroup.type (§4 SpecialInterestGroup — ISO 27001 control A.5.6)
export const SIG_TYPES = [
  'Professional association',
  'Standards body',
  'Industry consortium',
  'Threat intelligence sharing group',
  'Sector ISAC',
  'Vendor community',
  'Open-source community',
  'Academic research community',
  'Government-led forum',
  'Other',
];

// SpecialInterestGroup.purpose[] (§4 SpecialInterestGroup — ISO 27001 control A.5.6)
export const SIG_PURPOSES = [
  'Threat intelligence sharing',
  'Best-practice consultation',
  'Training and certification',
  'Standards development',
  'Networking and community',
  'Vulnerability disclosure coordination',
  'Vendor support and roadmap',
  'Sector incident coordination',
  'Research collaboration',
  'Other',
];

// SpecialInterestGroup.membershipStatus (§4 SpecialInterestGroup — ISO 27001 control A.5.6)
export const SIG_MEMBERSHIP_STATUSES = [
  'None',
  'Free or community',
  'Paid individual',
  'Paid corporate',
  'Sponsor',
];

// Customer.status (§4 Customer)
export const CUSTOMER_STATUSES = [
  'Prospective',
  'Active',
  'Paused',
  'Terminated',
];

// Customer.customerType (§4 Customer)
export const CUSTOMER_TYPES = [
  'Corporate',
  'SMB',
  'Public sector',
  'Academic',
  'Other',
];

// Customer.gdprRole (§4 Customer) — also reusable wherever a GDPR role
// classification is required.
export const GDPR_ROLES = [
  'Controller',
  'Processor',
  'Joint controller',
  'Not applicable',
];

// Customer.dataCategories[] suggested values — the TagInput accepts free input
// so this list seeds placeholders/help text but does not constrain saves.
export const DATA_CATEGORIES = [
  'PII general',
  'PII special',
  'Health',
  'Financial',
  'Minors',
  'Other',
];

// Incident.severity (§4 Incident)
export const SEVERITY_LEVELS = [
  'Critical',
  'High',
  'Medium',
  'Low',
];

// SoA controls[].implementationStatus (§4 Controls / SoA)
export const IMPLEMENTATION_STATUSES = [
  'Not implemented',
  'Partially implemented',
  'Implemented',
];

// Organization.certificationStatus (§4 Organization)
export const CERTIFICATION_STATUSES = [
  'none',
  'preparing',
  'stage1',
  'stage2',
  'certified',
  'surveillance',
  'expired',
];

// Organization.norms — the activated catalogue referenced by §4 and §10.
// Current active norm: iso27001. gdpr and nis2 activate conditional fields
// (dpo role, nis2Contact role, Incident notification timestamps) per §14
// Conditional fields pattern.
export const SUPPORTED_NORMS = [
  'iso27001',
  'gdpr',
  'nis2',
];

// Named accessors for the values inside SUPPORTED_NORMS. Consumers reference
// these rather than string literals so the `grep "'iso27001'|'gdpr'|'nis2'"`
// invariant holds everywhere outside this file.
export const [NORM_ISO27001, NORM_GDPR, NORM_NIS2] = SUPPORTED_NORMS;

// Organization.organizationType (§4 Organization)
export const ORGANIZATION_TYPES = [
  'public',
  'private',
  'mixed',
];

// Organization.publicSectorExposure (§4 Organization)
export const PUBLIC_SECTOR_EXPOSURE_VALUES = [
  'none',
  'contractor',
  'critical_service_provider',
  'mixed_subordinate',
];
